// Strip **bold**, *italic*, __underline__ markers for plain-text CSV output.
// Exported so the live word counter reuses this one stripper instead of
// defining its own (see utils/wordCount.js). Behavior unchanged.
export function stripMarkdown(s) {
  return (s || '')
    .replace(/\*\*(.+?)\*\*/gs, '$1')
    .replace(/__(.+?)__/gs, '$1')
    .replace(/\*([^*]+?)\*/gs, '$1');
}

// Shared script CSV export — used by Scripts.jsx toolbar and LiveProduction VideoCard
const FORMAT_COLS = {
  Skit: [
    { key: 'character', label: 'Character' },
    { key: 'line',      label: 'Line' },
    { key: 'notes',     label: 'Directional Notes' },
    { key: 'scene',     label: 'Scene Description' },
    { key: 'props',     label: 'Props' },
  ],
  TH: [
    { key: 'narration', label: 'Narration' },
    { key: 'aRoll',     label: 'A Roll' },
    { key: 'bRoll',     label: 'B Roll' },
    { key: 'vfx',       label: 'VFX' },
    { key: 'sfx',       label: 'SFX' },
    { key: 'props',     label: 'Props' },
  ],
  AI: [
    { key: 'narration', label: 'Narration' },
    { key: 'visuals',   label: 'Visuals' },
  ],
  Longform: [
    { key: 'sceneNumber', label: 'Scene #' },
    { key: 'character',   label: 'Character' },
    { key: 'line',        label: 'Line' },
    { key: 'scene',       label: 'Scene Description' },
  ],
  Mockumentary: [
    { key: 'character', label: 'Character' },
    { key: 'line',      label: 'Line' },
    { key: 'notes',     label: 'Directional Notes' },
    { key: 'aroll',     label: 'A-Roll' },
    { key: 'broll',     label: 'B-Roll' },
    { key: 'vfx',       label: 'FX' },
    { key: 'props',     label: 'Props' },
    { key: 'location',  label: 'Location' },
  ],
  // Recipe was missing here, so Recipe CSV export produced an empty file.
  Recipe: [
    { key: 'recipe',   label: 'Recipe' },
    { key: 'gestures', label: 'Gestures' },
  ],
};

// Production Notes column — mirrors Scripts.jsx. Appears in exports only once a
// script has been approved (script.hasBeenApproved). Kept out of FORMAT_COLS so
// the positional importers there are unaffected.
const PROD_NOTES_COL = { key: 'prodNotes', label: 'Production Notes' };
const PROD_NOTES_INSERT_IDX = { TH: 1, Skit: 2, Mockumentary: 2, Recipe: 1, Longform: 3 };
function displayCols(format, includeProdNotes) {
  const base = FORMAT_COLS[format] || [];
  const at = PROD_NOTES_INSERT_IDX[format];
  if (!includeProdNotes || at == null) return base;
  const out = base.slice();
  out.splice(at, 0, PROD_NOTES_COL);
  return out;
}

export function exportSceneCSV(script) {
  const fmt  = script.scriptBody?.format;
  const cols = displayCols(fmt, !!script.hasBeenApproved);
  if (!cols.length) return;
  const scenes = script.scriptBody?.scenes || [];

  const header = cols.map(c => `"${c.label}"`).join(',');
  const rows = scenes.map(row =>
    cols.map(c => {
      const val = stripMarkdown(row[c.key] || '').replace(/"/g, '""');
      return `"${val}"`;
    }).join(',')
  );

  const csv  = [header, ...rows].join('\n');
  const blob = new Blob(['\ufeff', csv], { type: 'text/csv' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href     = url;
  const safeName = (script.scriptName || 'script')
    .replace(/[^a-z0-9\s-]/gi, '').trim().replace(/\s+/g, '-');
  a.download = `${safeName}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
